import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import './TooltipProvider.css';

interface TooltipState {
  isVisible: boolean;
  content: string;
  position: { top: number; left: number };
}

interface TooltipContextType {
  showTooltip: (content: string, mouseX: number, mouseY: number) => void;
  hideTooltip: () => void;
  updatePosition: (mouseX: number, mouseY: number) => void;
}

const TooltipContext = createContext<TooltipContextType | null>(null);

export const useTooltip = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }
  return context;
};

interface TooltipProviderProps {
  children: React.ReactNode;
}

export const TooltipProvider: React.FC<TooltipProviderProps> = ({ children }) => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    isVisible: false,
    content: '',
    position: { top: 0, left: 0 }
  });
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculatePosition = (mouseX: number, mouseY: number, content: string) => {
    if (!tooltipRef.current) return { top: mouseY, left: mouseX };

    // 创建临时元素来测量tooltip尺寸
    const tempDiv = document.createElement('div');
    tempDiv.className = 'global-tooltip';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.position = 'fixed';
    tempDiv.style.top = '-9999px';
    tempDiv.innerHTML = `<div class="tooltip-content">${content}</div>`;
    document.body.appendChild(tempDiv);

    const rect = tempDiv.getBoundingClientRect();
    document.body.removeChild(tempDiv);

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    const offset = 12;
    let top = mouseY - rect.height - offset;
    let left = mouseX - rect.width / 2;

    // 防止tooltip超出视窗顶部
    if (top < 8) {
      top = mouseY + offset;
    }

    // 防止tooltip超出视窗左右边界
    if (left < 8) {
      left = 8;
    } else if (left + rect.width > viewport.width - 8) {
      left = viewport.width - rect.width - 8;
    }

    // 防止tooltip超出视窗底部
    if (top + rect.height > viewport.height - 8) {
      top = viewport.height - rect.height - 8;
    }

    return { top, left };
  };

  const showTooltip = (content: string, mouseX: number, mouseY: number) => {
    const position = calculatePosition(mouseX, mouseY, content);
    setTooltip({
      isVisible: true,
      content,
      position
    });
  };

  const hideTooltip = () => {
    setTooltip(prev => ({
      ...prev,
      isVisible: false
    }));
  };

  const updatePosition = (mouseX: number, mouseY: number) => {
    if (tooltip.isVisible && tooltip.content) {
      const position = calculatePosition(mouseX, mouseY, tooltip.content);
      setTooltip(prev => ({
        ...prev,
        position
      }));
    }
  };

  // 处理滚动和窗口大小变化
  useEffect(() => {
    const handleScroll = () => {
      if (tooltip.isVisible) {
        hideTooltip();
      }
    };

    const handleResize = () => {
      if (tooltip.isVisible) {
        hideTooltip();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [tooltip.isVisible]);

  return (
    <TooltipContext.Provider value={{ showTooltip, hideTooltip, updatePosition }}>
      {children}
      
      {tooltip.isVisible && (
        <div
          ref={tooltipRef}
          className="global-tooltip"
          style={{
            position: 'fixed',
            top: `${tooltip.position.top}px`,
            left: `${tooltip.position.left}px`,
            zIndex: 10000,
            pointerEvents: 'none'
          }}
        >
          <div className="tooltip-content">
            {tooltip.content}
          </div>
        </div>
      )}
    </TooltipContext.Provider>
  );
};
